import { useState } from "react";
import { Play, Volume2, Film } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { GOOGLE_REVIEWS, EMOTIONAL_MEDIA_IMAGE } from "@/data/content";

const IMAGE = EMOTIONAL_MEDIA_IMAGE;

export default function EmotionalMedia() {
  const [open, setOpen] = useState(false);
  const featured = GOOGLE_REVIEWS.find((r) => r.featured);

  return (
    <section data-testid="emotional-media" className="py-20 md:py-28 bg-[var(--maqam-surface)]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Copy */}
          <div className="lg:col-span-5">
            <span className="overline">The Heart of the Journey</span>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-[color:var(--maqam-navy)] leading-[1.08] tracking-tight">
              A Journey of Worship, Reflection &amp; Community
            </h2>
            <div className="mt-5 flex items-center gap-3">
              <span className="gold-divider" />
            </div>
            <p className="mt-6 text-[var(--maqam-muted)] text-base md:text-lg leading-relaxed">
              Umrah is more than reaching a destination. It is a journey of worship, renewal and
              connection—experienced alongside a community that supports you every step of the way.
            </p>

            {/* Pull quote — shows only when a real review exists; otherwise a gentle fallback */}
            {featured ? (
              <blockquote
                data-testid="emotional-pull-quote"
                className="mt-8 border-l-2 pl-5 font-serif text-xl md:text-2xl text-[color:var(--maqam-navy)] leading-snug"
                style={{ borderColor: "var(--maqam-gold)" }}
              >
                “{featured.text.length > 140 ? featured.text.slice(0, 140).trim() + "…" : featured.text}”
                <span className="mt-2 block text-xs uppercase tracking-[0.16em] text-[color:var(--maqam-gold-text)] not-italic">
                  — {featured.name}
                </span>
              </blockquote>
            ) : (
              <p
                data-testid="emotional-quote-placeholder"
                className="mt-8 border-l-2 pl-5 text-sm text-[var(--maqam-muted)] italic"
                style={{ borderColor: "var(--maqam-gold)" }}
              >
                Reflections from our pilgrims will appear here soon, straight from their verified reviews.
              </p>
            )}
          </div>

          {/* Media */}
          <div className="lg:col-span-7">
            <Dialog open={open} onOpenChange={setOpen}>
              <div className="relative rounded-3xl overflow-hidden review-shadow aspect-[16/10]">
                <img
                  src={IMAGE}
                  alt="A pilgrim in quiet worship and reflection before the Sacred House in Makkah"
                  loading="lazy"
                  width="1600"
                  height="1000"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{ background: "linear-gradient(180deg, rgba(11,35,65,0.10) 0%, rgba(11,35,65,0.55) 100%)" }}
                />
                <DialogTrigger asChild>
                  <button
                    data-testid="emotional-play-btn"
                    aria-label="Play reflective video preview (coming soon)"
                    className="absolute inset-0 flex flex-col items-center justify-center group"
                  >
                    <span className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/95 transition-transform group-hover:scale-105 group-focus-visible:scale-105">
                      <Play size={28} className="text-[color:var(--maqam-navy)] ml-1" style={{ fill: "currentColor" }} />
                    </span>
                    <span className="mt-4 text-white text-sm tracking-[0.14em] uppercase">
                      Watch a moment of reflection
                    </span>
                  </button>
                </DialogTrigger>

                {/* Silent-preview label — reassures no audio plays automatically */}
                <span className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-black/40 backdrop-blur-sm px-3 py-1.5 text-white/90 text-[11px] uppercase tracking-[0.14em]">
                  <Film size={12} /> Silent preview · no autoplay
                </span>
              </div>

              <DialogContent className="sm:max-w-md" data-testid="emotional-media-dialog">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl text-[color:var(--maqam-navy)]">
                    Reflective media, arriving soon
                  </DialogTitle>
                  <DialogDescription className="text-[var(--maqam-muted)] leading-relaxed">
                    We're preparing a short, respectful film of the journey — moments of worship,
                    reflection and community from real Maqam trips.
                  </DialogDescription>
                </DialogHeader>
                <div className="mt-2 rounded-xl border border-[var(--maqam-border)] bg-[var(--maqam-surface)] p-4 text-sm text-[var(--maqam-muted)] leading-relaxed">
                  <p className="flex items-start gap-2">
                    <Volume2 size={16} className="text-[color:var(--maqam-gold)] mt-0.5 flex-shrink-0" />
                    Any video will remain muted until you choose to play it, with visible controls and
                    captions. We will only feature recitations from a lawful, licensed source with
                    confirmed permission.
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </section>
  );
}
